import {
  AfterViewInit,
  Component, ComponentRef,
  DestroyRef,
  ElementRef,
  HostListener,
  inject, OnInit,
  Signal, viewChild,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import {TopBarComponent} from "../top-bar/top-bar.component";
import {MatFormField, MatHint, MatLabel, MatSuffix} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatButton, MatIconButton} from '@angular/material/button';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {AsyncPipe, NgClass, NgStyle} from '@angular/common';
import {FocusMeDirective} from '../../directives/focus-me.directive';
import {SupportApiService} from '../../services/support-api.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {finalize, map, pluck, tap} from 'rxjs';
import {TicketInterface} from '../../interfaces/http.interface';
import {SessionStorage, SessionStorageService} from 'ngx-webstorage';
import {CdkTextareaAutosize} from '@angular/cdk/text-field';
import {MatIcon} from '@angular/material/icon';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {SharedDataService} from '../../services/shared-data.service';
import {FileUploadComponentComponent} from '../file-upload-component/file-upload-component.component';
import {
  SelectTypeFileBottomsheetComponent
} from '../select-type-file-bottomsheet/select-type-file-bottomsheet.component';
import {MirandaApiService} from '../../services/miranda-api.service';
import {ServiceMiranda} from '../../interfaces/miranda.interface';
import {MatOption, MatSelect} from '@angular/material/select';
import {TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'app-create-ticket',
  imports: [
    TopBarComponent,
    MatFormField,
    MatInput,
    MatHint,
    MatButton,
    ReactiveFormsModule,
    NgStyle,
    FocusMeDirective,
    CdkTextareaAutosize,
    MatIcon,
    MatIconButton,
    MatSuffix,
    NgClass,
    AsyncPipe,
    FormsModule,
    MatSelect,
    MatLabel,
    MatOption
  ],
  templateUrl: './create-ticket.component.html',
  styleUrl: './create-ticket.component.scss'
})
export class CreateTicketComponent implements OnInit, AfterViewInit{
  public sendMessageLoading = false;
  private fb: FormBuilder = inject(FormBuilder);
  private supportApiService: SupportApiService = inject(SupportApiService);
  private mirandaApiService: MirandaApiService = inject(MirandaApiService);
  public sessionStorageService: SessionStorageService = inject(SessionStorageService);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private bottomSheet: MatBottomSheet = inject(MatBottomSheet);
  private translateService: TranslateService = inject(TranslateService);
  public sharedDataService: SharedDataService = inject(SharedDataService);
  public vcr: Signal<ViewContainerRef | undefined> = viewChild('container', {read: ViewContainerRef});
  public components: ComponentRef<FileUploadComponentComponent>[] = [];
  public orderId = this.sessionStorageService.retrieve('order_id');
  public sharedLoading$ = this.sharedDataService.sharedLoading$.asObservable();
  public ticketForm: FormGroup = this.fb.group({
    taxi_service_code: [this.sessionStorageService.retrieve('service'), Validators.required],
    severity: ['medium'],
    subject: [this.route.snapshot.queryParamMap.get('display_name')],
    department: [this.route.snapshot.queryParamMap.get('department')],
    category: [this.route.snapshot.queryParamMap.get('category')],
    files: [],
    // files: [
    //   {
    //     "url": "Посилання на файл",
    //     "name": "Назва файлу",
    //     "file_type": "Тип файлу (наприклад, image)"
    //   }
    // ],
    description: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(500)]]
  });
  public service: null | number = null;
  public serviceList: ServiceMiranda[] = [];


  @ViewChild('message') message!: ElementRef<HTMLTextAreaElement>;

  ngOnInit() {
    if(this.sessionStorageService.retrieve('userType') === 'dispatcher') {
      this.getServiceList();
    }
  }

  ngAfterViewInit() {
    this.sharedDataService.sharedLoading$.next(false);
    this.updateHeight(); // Виклик при завантаженні

    // Прокрутка при фокусі
    this.message.nativeElement.addEventListener('focus', () => {
      setTimeout(() => {
        this.message.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    });
  }

  @HostListener('window:resize')
  updateHeight() {
    document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
  }

  public submitForm() {
    this.sendMessageLoading = true;
    this.ticketForm.get('files')?.setValue(
      this.components
        .filter(c => c.instance.currentFileToUpload)
        .map(c => c.instance.currentFileToUpload)
    );
    let value = this.ticketForm.value;
    if(this.route.snapshot.queryParamMap.get('request_id')) {
      value = {...value, request_id: this.route.snapshot.queryParamMap.get('request_id')};
    }
    if(this.sessionStorageService.retrieve('userType') !== 'dispatcher') {
      value = {
        ...value,
        application: this.sessionStorageService.retrieve('application'),
        device: this.sessionStorageService.retrieve('device'),
      }
    }
    if(this.sessionStorageService.retrieve('userType') === 'dispatcher') {
      value = {
        ...value,
        taxi_service_code: this.sessionStorageService.retrieve('service').toString()
      }
    }

    this.supportApiService.createTicket(value)
      .pipe(
        map((chat) => chat.data),
        tap((chat) => {
          this.navigateToChat(chat);
        }),
        finalize(() => {
          this.sendMessageLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private navigateToChat(chat: TicketInterface) {
    this.router.navigate([`/chat`], {queryParams: {ticket_uuid: chat.id, status: chat.status}});
  }

  openBottomSheet(): void {
    this.bottomSheet.open(SelectTypeFileBottomsheetComponent, {
      autoFocus: false,
      panelClass: 'bottom-sheet-file-type',
    })
      .afterDismissed()
      .pipe(
        tap((result) => {
          if(result) {
            this.createComponent(result);
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private createComponent(typeFile: string) {
    const vcr = this.vcr();
    if (!vcr) return;

    const componentRef = vcr.createComponent(FileUploadComponentComponent);
    componentRef?.setInput('typeFile', typeFile);
    componentRef?.setInput('sendLoading', this.sendMessageLoading);
    componentRef.instance.getIndex = () => this.getComponentIndex(componentRef);
    componentRef.instance.remove = () => this.removeComponent(componentRef);
    componentRef.instance.loadingChange
      .pipe(
        tap((loading) => this.checkLoadingImage()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe()

    this.components.push(componentRef);
  }

  getComponentIndex(componentRef: ComponentRef<FileUploadComponentComponent>): number {
    return this.components.indexOf(componentRef);
  }

  removeComponent(componentRef: ComponentRef<FileUploadComponentComponent>) {
    const index = this.getComponentIndex(componentRef);
    if (index !== -1) {
      componentRef.destroy();
      this.components.splice(index, 1);
    }
  }

  private checkLoadingImage() {
    this.sharedDataService.sharedLoading$.next(this.components.some(c => c.instance.loading));
  }

  private getServiceList() {
    this.mirandaApiService.getOperatorServices().pipe(
      pluck('data'),
      map((services) => {
        if(services) {
          return services.map((service) => {
            if(service?.city?.loc_name) {
              service.city.loc_name = this.translateService.instant(service.city.loc_name) === 'No translation' ? service.city.loc_name : this.translateService.instant(service.city.loc_name);
            } else {
              service.city.loc_name = 'No translation';
            }
            return service;
          });
        } else {
          return services;
        }
      }),
      map((services) => {
        if(services) {
          return services.sort((a, b) => a?.city?.loc_name.localeCompare(b?.city?.loc_name));
        } else {
          return services;
        }
      }),
      tap(serviceList => {
        this.serviceList = serviceList;
        this.service = this.sessionStorageService.retrieve('service') ? +this.sessionStorageService.retrieve('service') : null;
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe()
  }

  protected changeService($event: any) {
    this.service = $event;
    this.sessionStorageService.store('service', this.service);
    this.ticketForm.get('taxi_service_code')?.setValue(this.service?.toString());
    this.ticketForm.updateValueAndValidity()
  }
}
